import firebase from "firebase/compat/app";
import "firebase/compat/database";
import { BrandSetupData } from "./brandSetup";
import { buildCustomizationFromBrandSetup } from "../../Admin/components/charitySetup/onboarding/onboardingCustomation/util";

const filterCustomizationFields = (
  data: Record<string, unknown>
): Record<string, unknown> =>
  Object.entries(data).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, unknown>);

function getLastCustomizationEditTime(brandSetup: BrandSetupData | null): number {
  if (!brandSetup) {
    return 0;
  }

  return Math.max(
    brandSetup.newsfeedLastSaved ?? 0,
    brandSetup.givingLastSaved ?? 0,
    brandSetup.postDetailsLastSaved ?? 0
  );
}

function hasCustomizationData(
  customization: Record<string, unknown> | null | undefined
): customization is Record<string, unknown> {
  return !!customization && Object.keys(customization).length > 0;
}

export function resolveCustomizationData(
  brandSetup: BrandSetupData | null,
  existingCustomization: Record<string, unknown> | null | undefined
): Record<string, unknown> {
  if (hasCustomizationData(existingCustomization)) {
    return existingCustomization;
  }

  const built = filterCustomizationFields(
    buildCustomizationFromBrandSetup(brandSetup)
  );

  if (Object.keys(built).length > 0) {
    return built;
  }

  return {};
}

export function getCurrentCustomization(
  customization: Record<string, unknown> | null | undefined,
  brandSetup: BrandSetupData | null | undefined,
  saveCustomization: (data: Record<string, unknown>) => Promise<void>
): Record<string, unknown> {
  if (!hasCustomizationData(customization) && !brandSetup) {
    return {};
  }

  const resolved = resolveCustomizationData(brandSetup ?? null, customization);

  if (!hasCustomizationData(customization) && Object.keys(resolved).length > 0) {
    saveCustomization(resolved);
  }

  return resolved;
}

export function shouldRebuildCustomizationFromBrandSetup(
  brandSetup: BrandSetupData | null,
  existingCustomization: Record<string, unknown> | null,
  builtFromBrandSetup: Record<string, unknown>
): boolean {
  if (Object.keys(builtFromBrandSetup).length === 0) {
    return false;
  }

  if (!hasCustomizationData(existingCustomization)) {
    return true;
  }

  if (!brandSetup) {
    return false;
  }

  const brandSetupUpdatedAt = brandSetup.updatedAt ?? 0;
  const lastCustomizationEdit = getLastCustomizationEditTime(brandSetup);

  return brandSetupUpdatedAt > lastCustomizationEdit;
}

export function customizationHasUsableKeys(
  data: Record<string, unknown> | null | undefined
): boolean {
  if (!data) {
    return false;
  }
  return Object.keys(data).some(key => {
    const value = data[key];
    if (value === null || value === undefined || value === "") {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  });
}

export async function writeHydratedCustomization(
  charityId: string,
  data: Record<string, unknown>
): Promise<void> {
  if (!charityId) {
    throw new Error("Charity ID is required");
  }
  const filtered = filterCustomizationFields(data);
  if (Object.keys(filtered).length === 0) {
    return;
  }
  const database = firebase.database();
  await database.ref(`${charityId}/charitySetup/customization`).update(filtered);
}

export async function ensureCustomizationData(
  charityId: string,
  options?: { replaceExisting?: boolean }
): Promise<Record<string, unknown>> {
  if (!charityId) {
    throw new Error("Charity ID is required");
  }

  const database = firebase.database();
  const customizationRef = database.ref(`${charityId}/charitySetup/customization`);
  const brandSetupRef = database.ref(`${charityId}/charitySetup/brandSetup`);

  const [customizationSnapshot, brandSetupSnapshot] = await Promise.all([
    customizationRef.once("value"),
    brandSetupRef.once("value"),
  ]);
  const charitySetupSnapshot = await database.ref(`${charityId}/charitySetup`).once("value");

  const existingCustomization = customizationSnapshot.val() as
    | Record<string, unknown>
    | null;
  const brandSetup = brandSetupSnapshot.val() as BrandSetupData | null;
  const charitySetup = charitySetupSnapshot.val() as { name?: string } | null;
  const built = filterCustomizationFields(
    buildCustomizationFromBrandSetup(brandSetup)
  );

  const rebuildFromBrand =
    Boolean(options?.replaceExisting) &&
    shouldRebuildCustomizationFromBrandSetup(
      brandSetup,
      existingCustomization,
      built
    );

  let resolved = rebuildFromBrand && Object.keys(built).length > 0
    ? { ...built }
    : resolveCustomizationData(brandSetup, existingCustomization);

  if (charitySetup?.name && !resolved.organizationName) {
    resolved = { ...resolved, organizationName: charitySetup.name };
  }
  if (
    (resolved.organizationName || charitySetup?.name) &&
    !resolved.newsfeedHeaderTitle
  ) {
    resolved = {
      ...resolved,
      newsfeedHeaderTitle: resolved.organizationName || charitySetup?.name,
    };
  }

  const shouldWrite =
    Object.keys(resolved).length > 0 &&
    (rebuildFromBrand || !hasCustomizationData(existingCustomization));

  if (shouldWrite) {
    await customizationRef.set(resolved);
  }

  return resolved;
}
